/**
 * UniversalResourcePage - універсальний компонент для відображення всіх типів сторінок ресурсів
 * 
 * Замінює 5 окремих компонентів:
 * - UniversalResourceListPage
 * - UniversalResourceCreatePage
 * - UniversalResourceEditPage
 * - UniversalResourceViewPage
 * - UniversalResourceCardPage
 * 
 * Логіка роботи:
 * 1. Парсить URL і визначає ресурс та дію (list/create/view/edit)
 * 2. Для списку - просто рендерить ResourceList
 * 3. Для карточок:
 *    a) Якщо режим page - рендерить ResourceDetail замість списку
 *    b) Якщо режим drawer/modal - рендерить список + автоматично викликає OpenResourceCardTask
 * 4. Передає listCrudContext в карточку для прямої комунікації
 * 
 * Переваги:
 * - Єдиний контекст для списку і всіх його карточок
 * - Карточки по прямому посиланню відкриваються правильно (drawer/modal над списком)
 * - Можливість оновлювати список з карточки напряму через listCrudContext
 * - Спрощення коду - один компонент замість п'яти
 * 
 * Архітектура:
 * - Використовує React hooks (useMemo) замість MobX observer
 * - useLocation() з React Router автоматично викликає ререндер при зміні URL
 * - parseResourceUrl() парсить URL, OpenResourceCardTask() відкриває карточки
 */

import React, { useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router'
import { ResourceList } from '@/Core/UI/CrudResource/ResourceList'
import { ResourceDetail } from '@/Core/UI/CrudResource/ResourceDetail'
import { useNavigationContext } from '@/Core/Providers/NavigationProvider'
import { createCrudResourceContext } from '@/Core/UI/CrudResource/CrudResourceContextProvider'
import { generateRegistryKey } from '@/Core/Utils/generateRegistryKey'
import { parseResourceUrl } from '@/Core/Tasks/Resources/ParseResourceUrlTask'
import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { applyInitialFilter } from '@/Core/UI/CrudResource/Helpers/applyInitialFilter'
import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'

interface ResourceListComponentProps {
    urlInfo: ReturnType<typeof parseResourceUrl>
    // Початкові значення фільтра, прочитані з query-параметрів URL
    // (напр. перехід з головної: /requests?status=new). null → без фільтра.
    filterInitialValues: Record<string, any> | null
}

// Компонент для списку (БЕЗ observer - React сам ререндерить через props)
const ResourceListComponent: React.FC<ResourceListComponentProps> = ({ urlInfo, filterInitialValues }) => {
    const cardOpenedRef = useRef(false)

    // Серіалізуємо initialValues у стабільний ключ для useMemo-залежності,
    // щоб контекст перестворювався при зміні query-фільтра в URL.
    const filterKey = filterInitialValues ? JSON.stringify(filterInitialValues) : ''

    // Створюємо контекст для списку з автоматично згенерованим ключем
    const listContext = useMemo(() => {
        if (!urlInfo.resourceConfig) return null

        // Автоматична генерація ключа для URL-based списку
        const autoRegistryKey = generateRegistryKey.urlList(urlInfo.resourceConfig.key)

        const ctx = createCrudResourceContext({
            contextType: 'list',
            registryKey: autoRegistryKey, // Використовуємо автоматично згенерований ключ
            resource: urlInfo.resourceConfig,
            prefilter: urlInfo.resourceConfig.prefilter,
        } as any)

        // Початковий фільтр з query застосовуємо як звичайний користувацький
        // фільтр (НЕ initialValues), щоб кнопка «Очистити» могла його скинути.
        applyInitialFilter(ctx, filterInitialValues)

        return ctx
    }, [urlInfo.resourceConfig?.key, filterKey])
    
    // Автоматично відкриваємо карточку при прямому посиланні на drawer/modal
    useEffect(() => {
        if (
            urlInfo.resourceConfig &&
            urlInfo.action !== 'list' &&
            (urlInfo.displayMode === 'drawer' || urlInfo.displayMode === 'modal') &&
            listContext &&
            !cardOpenedRef.current
        ) {
            cardOpenedRef.current = true
            
            const params: any = {
                resource: urlInfo.resourceConfig,
                mode: urlInfo.action,
                displayMode: urlInfo.displayMode,
                listCrudContext: listContext,
                forceDisplayMode: true,
            }
            
            // Додаємо primaryFieldValue тільки якщо він є
            if (urlInfo.resourceId) {
                params.primaryFieldValue = urlInfo.resourceId
            }
            
            OpenResourceCardTask(params)
        }
        
        // Скидаємо прапорець при зміні ресурсу
        return () => {
            if (urlInfo.action === 'list') {
                cardOpenedRef.current = false
            }
        }
    }, [urlInfo, listContext])
    
    if (!urlInfo.resourceConfig || !listContext) return null

    return <ResourceList resource={urlInfo.resourceConfig} crudContext={listContext as any} registryKey={generateRegistryKey.urlList(urlInfo.resourceConfig.key)} />
}

/**
 * Перетворює query-рядок URL у початкові значення фільтра списку.
 *
 * Бере лише поля, оголошені у filter.includeFields ресурсу (білий список),
 * щоб сторонні query-параметри не потрапляли у фільтр. Значення 'true'/'false'
 * приводяться до boolean (фільтри-перемикачі), решта лишається рядком.
 * Повертає null, якщо валідних параметрів немає.
 */
function parseFilterFromQuery(
    search: string,
    resourceConfig: IResourceConfig | null,
): Record<string, any> | null {
    if (!search || !resourceConfig) return null

    const allowed: string[] | undefined = (resourceConfig as any)?.filter?.includeFields
    const params = new URLSearchParams(search)
    const result: Record<string, any> = {}

    params.forEach((value, key) => {
        if (Array.isArray(allowed) && !allowed.includes(key)) return
        if (value === '') return
        result[key] = value === 'true' ? true : value === 'false' ? false : value
    })

    return Object.keys(result).length > 0 ? result : null
}

interface ResourceDetailComponentProps {
    urlInfo: ReturnType<typeof parseResourceUrl>
}

// Компонент для page карточки (БЕЗ observer - React сам ререндерить через props)
const ResourceDetailComponent: React.FC<ResourceDetailComponentProps> = ({ urlInfo }) => {
    if (!urlInfo.resourceConfig) return null

    // Автоматична генерація ключа для URL-based карточки
    const autoRegistryKey = generateRegistryKey.urlCard(
        urlInfo.resourceConfig.key,
        urlInfo.action as any,
        urlInfo.resourceId
    )

    const ResourceDetailAny = ResourceDetail as any

    return (
        <ResourceDetailAny
            resource={urlInfo.resourceConfig}
            mode={urlInfo.action}
            itemId={urlInfo.resourceId}
            displayMode="page"
            registryKey={autoRegistryKey}
        />
    )
}

const UniversalResourcePage: React.FC = () => {
    const location = useLocation()
    const { isProgrammaticNavigation } = useNavigationContext()
    
    // Парсимо URL через useMemo (перераховується при зміні pathname)
    const urlInfo = useMemo(
        () => parseResourceUrl(location.pathname, isProgrammaticNavigation()),
        [location.pathname, isProgrammaticNavigation]
    )

    // Початковий фільтр з query-параметрів (напр. /requests?status=new з головної)
    const filterInitialValues = useMemo(
        () => parseFilterFromQuery(location.search, urlInfo.resourceConfig),
        [location.search, urlInfo.resourceConfig]
    )

    // Ресурс не знайдено
    if (!urlInfo.resourceConfig) {
        return <div>Ресурс не знайдено</div>
    }

    // Список або drawer/modal карточка (показуємо список + карточка відкривається автоматично)
    if (urlInfo.action === 'list' || urlInfo.displayMode === 'drawer' || urlInfo.displayMode === 'modal') {
        return <ResourceListComponent urlInfo={urlInfo} filterInitialValues={filterInitialValues} />
    }
    
    // Page карточка (замість списку)
    return <ResourceDetailComponent urlInfo={urlInfo} />
}

export default UniversalResourcePage
